import React, {useState, useEffect, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import {Button, Modal} from '@/components/atoms';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  addMediaTitle,
  setMediaOptIn,
  resetSalesTargetDisplayContentMedia,
  setSalesTargetDisplayContentMedia,
  addMediaId,
  setSalesTargetDisplayContentMediaOptional,
  setDisplayContentMediaOptionalButton,
  addMediaTitleMediaFileOptional,
  addMediaIdMediaFileOptional,
  setMediaOptInMediaFileOptional,
  resetSalesTargetDisplayContentMediaOptional
} from '@/store/slices/salesTargetSlice';
import {MediaCardForm} from '@/components/organisms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {UploadFile} from '@/components/molecules';
//import { useFileUpload } from '@/hooks/useFileUpload'
import {
  ALERT_TYPES,
  Constants,
  UPLOAD_MEDIA_DEBOUNCE_TIME,
  UPLOAD_MEDIA_ACCEPTED_EXTENSIONS
} from '@/utils/constants/commonConstants';
import {IMAGETYPE} from '@/utils/constants/saleTargetConstants';
import {convertImageToWebP} from '@/helpers/shadcn/utils';
import {getFileVerification} from '@/services/fileUploadService';

function UploadMedia({
  setMediaCardFormData,
  mediaCardFormData,
  uploadFor = null,
  MediaCardUiSchema,
  title,
  fileType,
  label
}) {
  const dispatch = useAppDispatch();
  const countryCode = useAppSelector((state) => state?.common?.countryCode);

  const initialFileErrorState = useMemo(
    () => ({feedbackType: ALERT_TYPES.Info, helperText: ''}),
    []
  );

  // All useState calls at the top level
  const [fileError, setFileError] = useState(initialFileErrorState);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Foreground image states
  const [openForeground, setOpenForeground] = useState(false);
  const [mediaFileForeground, setMediaFileForeground] = useState([]);

  // Background image states
  const [openBackground, setOpenBackground] = useState(false);
  const [mediaFileBackground, setMediaFileBackground] = useState([]);

  // Redux selectors
  const contentMedia = useAppSelector(
    (state) => state.salesTarget.displayContentMedia
  );
  const contentMediaOptional = useAppSelector(
    (state) => state.salesTarget.displayContentMediaOptional
  );
  const isOptionalBtnEnable = useAppSelector(
    (state) => state.salesTarget.displayContentMediaOptionalButton
  );

  // Memoized configuration object
  const imageConfig = useMemo(
    () => ({
      foreground: {
        key: 'foreground',
        isOpen: openForeground,
        setIsOpen: setOpenForeground,
        mediaFile: mediaFileForeground,
        setMediaFile: setMediaFileForeground,
        contentMedia: contentMedia,
        imageType: IMAGETYPE.Foreground,
        setContentMedia: setSalesTargetDisplayContentMedia,
        resetContentMedia: resetSalesTargetDisplayContentMedia,
        addMediaTitle: addMediaTitle,
        addMediaId: addMediaId,
        setMediaOptIn: setMediaOptIn,
        setOptionalButton: () =>
          dispatch(setDisplayContentMediaOptionalButton(true)),
        resetOptionalButton: () =>
          dispatch(setDisplayContentMediaOptionalButton(false)),
        showButton: !contentMedia?.imageUri,
        showMediaCard: Boolean(contentMedia?.imageUri || contentMedia?.imageUrl)
      },
      background: {
        key: 'background',
        isOpen: openBackground,
        setIsOpen: setOpenBackground,
        mediaFile: mediaFileBackground,
        setMediaFile: setMediaFileBackground,
        contentMedia: contentMediaOptional,
        imageType: IMAGETYPE.Background,
        setContentMedia: setSalesTargetDisplayContentMediaOptional,
        resetContentMedia: resetSalesTargetDisplayContentMediaOptional,
        addMediaTitle: addMediaTitleMediaFileOptional,
        addMediaId: addMediaIdMediaFileOptional,
        setMediaOptIn: setMediaOptInMediaFileOptional,
        setOptionalButton: () =>
          dispatch(setDisplayContentMediaOptionalButton(false)),
        resetOptionalButton: () =>
          dispatch(setDisplayContentMediaOptionalButton(true)),
        showButton: isOptionalBtnEnable,
        showMediaCard: Boolean(
          contentMediaOptional?.imageUri || contentMediaOptional?.imageUrl
        )
      }
    }),
    [
      openForeground,
      openBackground,
      mediaFileForeground,
      mediaFileBackground,
      contentMedia,
      contentMediaOptional,
      isOptionalBtnEnable,
      dispatch
    ]
  );

  // Memoized handlers
  const handleCloseModal = useCallback(
    (config) => {
      dispatch(config.resetContentMedia());
      config.setMediaFile([]);
      config.setIsOpen(false);
      setFileError(initialFileErrorState);
    },
    [dispatch, initialFileErrorState]
  );

  const handleOpenModal = useCallback((config) => {
    config.setIsOpen(true);
  }, []);

  const handleUpload = useCallback(
    async (file, config) => {
      setError(false);
      setFileError(initialFileErrorState);
      const converted = await convertImageToWebP(file, {entityType: 'rewards'});
      const webpFile = converted.webpFile;
      const metadata = converted.metadata;

      const metadataPayload = {
        mediaUploadMetadata: [
          {
            id: metadata.id,
            referenceEntity: metadata.referenceEntity,
            originalFileName: metadata.originalFileName,
            originalFormat: metadata.originalFormat,
            originalSize: metadata.originalSize,
            width: metadata.width,
            height: metadata.height,
            blobFileSize: metadata.blobFileSize,
            blobFormat: metadata.blobFormat
          }
        ]
      };

      const formData = new FormData();
      formData.append('images', webpFile);
      formData.append('metadata', JSON.stringify(metadataPayload));

      const response = await getFileVerification(
        formData,
        Constants.SalesTarget,
        undefined, // onUploadProgress
        'webp', // type
        {countryCode} // pass as extra headers
      );
      const cdnUrl = response?.[0]?.media?.cdnUrl;
      if (cdnUrl) {
        config.setMediaFile([cdnUrl]);
        dispatch(
          config.setContentMedia({
            ...config.contentMedia,
            imageUri: cdnUrl,
            imageUrl: cdnUrl,
            title: response?.[0]?.media?.filename,
            imageId: config.imageType
          })
        );
        config.setOptionalButton();
      }
      return response;
    },
    [dispatch, countryCode, initialFileErrorState]
  );

  const handleError = useCallback(() => {
    setFileError({
      feedbackType: ALERT_TYPES.Error,
      helperText: 'Please use a valid PNG/JPG or WebP file'
    });
  }, []);

  const handleDelete = useCallback(
    (config) => {
      config.setMediaFile([]);
      dispatch(config.resetContentMedia());
      config.resetOptionalButton();
    },
    [dispatch]
  );

  const handleDone = useCallback((config) => {
    config.setIsOpen(false);
  }, []);

  const debouncedHandleMediaCardChange = debounce((data, config) => {
    if (data?.title !== undefined) {
      dispatch(config.addMediaTitle(data?.title));
    }

    if (data?.imageId !== undefined) {
      dispatch(config.addMediaId(data?.imageId));
    }
    if (data?.optIn !== undefined) {
      dispatch(config.setMediaOptIn(data?.optIn));
    }

    // Only update mediaCardFormData for foreground
    if (config.key === 'foreground') {
      setMediaCardFormData((prevData) => ({
        ...prevData,
        ...data
      }));
    }
  }, UPLOAD_MEDIA_DEBOUNCE_TIME);

  const handleMediaCardChange = useCallback(
    (data, config) => {
      debouncedHandleMediaCardChange(data, config);
    },
    [debouncedHandleMediaCardChange]
  );

  // Update mediaFile state when contentMedia changes
  useEffect(() => {
    if (contentMedia?.imageUrl) {
      setMediaFileForeground([contentMedia?.imageUrl]);
    }
  }, [contentMedia?.imageUrl]);

  useEffect(() => {
    if (contentMediaOptional?.imageUrl) {
      setMediaFileBackground([contentMediaOptional?.imageUrl]);
    }
  }, [contentMediaOptional?.imageUrl]);

  // Render modal for a given configuration
  const renderModal = (config) => (
    <Modal
      key={config.key}
      type={MODAL_TYPE.SMALL}
      isDark={true}
      onCloseModal={() => handleCloseModal(config)}
      onClickSecondaryButton={() => handleCloseModal(config)}
      onClickPrimaryButton={() => handleDone(config)}
      primaryButtonText='Done'
      showTertiaryButton={false}
      showAuxiliaryButton={false}
      showSecondaryButton={true}
      showLink={false}
      showModal={config.isOpen}
      title={'Upload Media'}
      primaryButtonDisabled={
        config.mediaFile?.length === 0 ||
        (Object.keys(fileError)?.length > 0 &&
          fileError?.feedbackType === ALERT_TYPES.Error) ||
        error
      }
      secondaryButtonDisabled={config.mediaFile?.length > 0 || uploading}
      hideCloseButton={uploading}
      content={
        <div className='media-upload'>
          <UploadFile
            uploadFor={uploadFor}
            acceptedExtensions={UPLOAD_MEDIA_ACCEPTED_EXTENSIONS}
            description={
              <span>
                Drag and drop your file here <br /> (only .jpg,.png,.webp files){' '}
                <br /> or click here to upload from My Files
              </span>
            }
            label='Upload image file'
            variant={Constants.Edit}
            buttonLabel='Select from System Library'
            onUploadError={handleError}
            feedbackType={fileError.feedbackType}
            helperText={fileError.helperText}
            type='image'
            required={true}
            imageUri={config.contentMedia?.imageUri}
            setFile={config.setMediaFile}
            uploadFileSuccess={config.setContentMedia}
            resetUploadFile={config.resetContentMedia}
            onUpload={(file) => handleUpload(file, config)}
            fileSizeCheck={true}
            onSetError={setError}
            setFileErrorMessage={setFileError}
            fileType={fileType}
            uploading={uploading}
            setUploading={setUploading}
          />
        </div>
      }
    />
  );

  // Render single upload button that handles both foreground and background
  const renderUploadButton = useCallback(() => {
    const foregroundHasImage = Boolean(
      contentMedia?.imageUri || contentMedia?.imageUrl
    );
    const backgroundHasImage = Boolean(
      contentMediaOptional?.imageUri || contentMediaOptional?.imageUrl
    );
    const bothImagesPresent = foregroundHasImage && backgroundHasImage;

    const handleUploadClick = () => {
      if (!foregroundHasImage) {
        handleOpenModal(imageConfig.foreground);
      }
      // If background doesn't have image, upload background
      else if (!backgroundHasImage) {
        handleOpenModal(imageConfig.background);
      }
    };

    return (
      <div className='mb-6 flex flex-wrap py-2' data-testid='uploadMediaBtn'>
        <Button
          handleButton={handleUploadClick}
          uiSchema={{
            size: 'large',
            title: 'Upload Media',
            variant: 'primary',
            disabled: bothImagesPresent
          }}
        />
      </div>
    );
  }, [contentMedia, contentMediaOptional, handleOpenModal, imageConfig]);

  // Render media card form for a given configuration
  const renderMediaCardForm = useCallback(
    (config) =>
      config.showMediaCard && (
        <MediaCardForm
          imageUri={
            config.contentMedia?.imageUri ?? config.contentMedia?.imageUrl
          }
          handleChange={(data) => handleMediaCardChange(data, config)}
          handleDelete={() => handleDelete(config)}
          mediaCardFormData={
            config.key === 'foreground'
              ? mediaCardFormData
              : config.contentMedia
          }
          MediaCardUiSchema={MediaCardUiSchema}
          label={label}
        />
      ),
    [
      handleMediaCardChange,
      handleDelete,
      mediaCardFormData,
      MediaCardUiSchema,
      label
    ]
  );

  return (
    <>
      <h1 className='mb-4 text-sm font-bold'>{title}</h1>

      {renderModal(imageConfig.foreground)}
      {renderModal(imageConfig.background)}
      <div className='flex flex-col gap-6 md:flex-row md:gap-16'>
        {/* Foreground Image Section */}
        {renderMediaCardForm(imageConfig.foreground)}

        {/* Background Image Section */}
        {renderMediaCardForm(imageConfig.background)}
      </div>
      {/* Upload Button Section */}
      {renderUploadButton()}
    </>
  );
}

UploadMedia.propTypes = {
  setMediaCardFormData: PropTypes.func.isRequired,
  mediaCardFormData: PropTypes.shape({
    title: PropTypes.string,
    imageId: PropTypes.string,
    optIn: PropTypes.bool
  }).isRequired,
  uploadFor: PropTypes.string,
  MediaCardUiSchema: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  fileType: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

export default React.memo(UploadMedia);
